import { useState } from "react";
import { format } from "date-fns";
import { Minus, Plus, Trash2, ShoppingBag, CalendarIcon, ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const field = "w-full rounded-xl border border-black/20 bg-white px-4 py-3 font-500 focus-visible:ring-2 focus-visible:ring-brand-coral";

export default function BookingDialog() {
  const { open, setOpen, items, removeItem, updateQty, updateDays, subtotal, clear } = useCart();
  const [step, setStep] = useState(0);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", delivery_address: "", notes: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const deposit = subtotal * 0.3;

  const checkout = async () => {
    if (!form.customer_name || !form.email || !form.phone || !form.delivery_address) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (!date) {
      toast.error("Please pick your event date.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        event_date: format(date, "PPP"),
        event_date_iso: format(date, "yyyy-MM-dd"),
        items: items.map((i) => ({ item_id: i.item_id, quantity: i.quantity, days: i.days })),
        origin_url: window.location.origin,
      };
      const res = await api.post("/bookings", payload);
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-lg bg-cream flex flex-col p-0" data-testid="booking-sheet">
        <SheetHeader className="p-6 border-b border-black/10">
          <SheetTitle className="font-display font-900 text-2xl tracking-tight flex items-center gap-2">
            <ShoppingBag className="text-brand-coral" /> {step === 0 ? "Your Booking" : "Almost There"}
          </SheetTitle>
          <SheetDescription className="font-500 text-ink/50">
            {step === 0 ? "Adjust quantities and rental days, then continue." : "Add your details and pick an event date."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-20" data-testid="empty-cart">
              <ShoppingBag size={48} className="mx-auto text-ink/20" />
              <p className="mt-4 font-600 text-ink/50">Your booking is empty. Add some party gear!</p>
            </div>
          ) : step === 0 ? (
            items.map((i) => (
              <div key={i.item_id} className="flex gap-4 rounded-2xl border border-black/10 bg-white p-3" data-testid={`cart-item-${i.item_id}`}>
                <img src={i.image} alt={i.name} className="h-20 w-20 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-display font-800 text-base leading-tight">{i.name}</h4>
                    <button onClick={() => removeItem(i.item_id)} className="text-ink/40 hover:text-brand-coral" data-testid={`remove-${i.item_id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="mt-1 text-sm text-ink/50 font-500">${i.price_per_day} / {i.unit}</div>
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-600 text-ink/50">Qty</span>
                      <Stepper value={i.quantity} onChange={(v) => updateQty(i.item_id, v)} id={i.item_id} k="qty" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-600 text-ink/50">Days</span>
                      <Stepper value={i.days} onChange={(v) => updateDays(i.item_id, v)} id={i.item_id} k="days" />
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="space-y-4" data-testid="booking-form">
              <Input className={field} placeholder="Full name *" value={form.customer_name} onChange={set("customer_name")} data-testid="booking-name" />
              <div className="grid grid-cols-2 gap-3">
                <Input className={field} type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="booking-email" />
                <Input className={field} placeholder="Phone *" value={form.phone} onChange={set("phone")} data-testid="booking-phone" />
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <button className={`${field} flex items-center justify-between text-left ${!date && "text-ink/40"}`} data-testid="booking-date-trigger">
                    {date ? format(date, "PPP") : "Select event date *"}
                    <CalendarIcon size={18} className="text-brand-coral" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                </PopoverContent>
              </Popover>
              <Input className={field} placeholder="Delivery address *" value={form.delivery_address} onChange={set("delivery_address")} data-testid="booking-address" />
              <Textarea className={field} rows={3} placeholder="Anything else we should know? (optional)" value={form.notes} onChange={set("notes")} data-testid="booking-notes" />
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-black/10 p-6 space-y-3 bg-white">
            <div className="flex items-center justify-between text-sm font-500 text-ink/60">
              <span>Rental subtotal</span>
              <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between font-800 text-lg">
              <span>Deposit due now (30%)</span>
              <span className="text-brand-coral" data-testid="cart-deposit">${deposit.toFixed(2)}</span>
            </div>
            <p className="text-xs text-ink/40 font-500 flex items-center gap-1.5">
              <ShieldCheck size={14} /> Remaining ${(subtotal - deposit).toFixed(2)} due on delivery day.
            </p>

            {step === 0 ? (
              <button
                onClick={() => setStep(1)}
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-ink text-white py-4 font-700 hover:bg-brand-coral transition-colors"
                data-testid="cart-continue"
              >
                Continue to Details <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
            ) : (
              <div className="flex gap-3">
                <button onClick={() => setStep(0)} className="flex items-center justify-center gap-2 rounded-full border-2 border-ink px-5 py-4 font-700 hover:bg-ink hover:text-white transition-colors" data-testid="cart-back">
                  <ArrowLeft size={18} />
                </button>
                <button
                  onClick={checkout}
                  disabled={loading}
                  className="group flex flex-1 items-center justify-center gap-2 rounded-full bg-brand-coral text-white py-4 font-700 hover:bg-ink transition-colors disabled:opacity-60"
                  data-testid="cart-checkout"
                >
                  {loading ? "Redirecting to Stripe..." : `Pay Deposit $${deposit.toFixed(2)}`}
                </button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Stepper({ value, onChange, id, k }) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-black/15 px-1">
      <button onClick={() => onChange(value - 1)} className="grid place-items-center h-6 w-6 rounded-full hover:bg-brand-coral hover:text-white transition-colors" data-testid={`dec-${k}-${id}`}>
        <Minus size={12} />
      </button>
      <span className="w-5 text-center font-700 text-sm" data-testid={`val-${k}-${id}`}>{value}</span>
      <button onClick={() => onChange(value + 1)} className="grid place-items-center h-6 w-6 rounded-full hover:bg-brand-coral hover:text-white transition-colors" data-testid={`inc-${k}-${id}`}>
        <Plus size={12} />
      </button>
    </div>
  );
}
